import React, { useState, useEffect } from "react";
import { Clock, CheckCircle, Search, FileText, CreditCard, Calendar, User, Users, MapPin, BookOpen, Wallet } from "lucide-react";
import "./PaymentHistory.css";
import { fetchPaymentHistory, downloadReceipt } from "../../services/invoiceService";

const PaymentHistory = () => {
    const [history, setHistory] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [loadingAction, setLoadingAction] = useState({ id: null });
    const [pdfConfig, setPdfConfig] = useState({ show: false, data: null, title: "" });
    const [popup, setPopup] = useState({ show: false, title: "", message: "", type: "error" });

    useEffect(() => {
        loadHistory();
    }, []);

    const loadHistory = async () => {
        try {
            setIsLoading(true);
            const data = await fetchPaymentHistory();
            setHistory(data || []);
        } catch (err) {
            console.error("Failed to load payment history", err);
            setPopup({
                show: true,
                title: "Error",
                message: "Failed to load payment history.",
                type: "error"
            });
        } finally {
            setIsLoading(false);
        }
    };

    const handleViewReceipt = async (inv) => {
        try {
            setLoadingAction({ id: inv.InvoiceId });
            const res = await downloadReceipt(inv.InvoiceId);

            if (res && res.file) {
                const byteCharacters = atob(res.file);
                const byteNumbers = new Array(byteCharacters.length);
                for (let i = 0; i < byteCharacters.length; i++) {
                    byteNumbers[i] = byteCharacters.charCodeAt(i);
                }
                const byteArray = new Uint8Array(byteNumbers);
                const blob = new Blob([byteArray], { type: 'application/pdf' });
                const blobUrl = URL.createObjectURL(blob);

                const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

                if (isMobile) {
                    const newWindow = window.open(blobUrl, '_blank');
                    if (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined') {
                        const link = document.createElement('a');
                        link.href = blobUrl;
                        link.target = '_blank';
                        link.click();
                    }
                } else {
                    setPdfConfig({
                        show: true,
                        data: blobUrl,
                        title: `Receipt #${inv.InvoiceNumber}`
                    });
                }
            }
        } catch (err) {
            console.error("Failed to load document", err);
            setPopup({
                show: true,
                title: "Load Error",
                message: "Could not retrieve the receipt.",
                type: "error"
            });
        } finally {
            setLoadingAction({ id: null });
        }
    };

    const filteredHistory = history.filter(inv => 
        inv.InvoiceNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        inv.StudentName?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <div className="payment-history-container">
            <div className="payment-history-header">
                <h2>Payment History</h2>
                <div className="history-search">
                    <Search size={18} />
                    <input 
                        type="text" 
                        placeholder="Search by invoice # or student name..." 
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>
            </div>

            <div className="history-content-area">
                {isLoading && (
                    <div className="history-loader">
                        <div className="spinner"></div>
                        <p>Loading History...</p>
                    </div>
                )}

                {!isLoading && filteredHistory.length === 0 && (
                    <div className="empty-state">
                        <CreditCard size={48} />
                        <h3>No History Found</h3>
                        <p>There are no past payments available.</p>
                    </div>
                )}

                <div className="history-cards-grid">
                    {filteredHistory.map((inv, index) => (
                        <div className="history-premium-card" key={`${inv.InvoiceId}-${index}`}>
                            <div className="card-top-strip">
                                <div className="inv-badge">
                                    <FileText size={14} />
                                    <span>{inv.InvoiceNumber}</span>
                                </div>
                                <div className={`status-pill ${inv.Status?.toLowerCase() === 'success' ? 'paid' : 'pending'}`}>
                                    {inv.Status?.toLowerCase() === 'success' ? <CheckCircle size={14} /> : <Clock size={14} />}
                                    <span>{inv.Status || 'SUCCESS'}</span>
                                </div>
                            </div>

                            <div className="card-body-content">
                                <div className="details-wrap">
                                    <div className="student-header-row">
                                        <h3 title={inv.StudentName}>{inv.StudentName}</h3>
                                        <div className="amount-text">
                                            <span className="currency">₹</span>
                                            <span className="value">{parseFloat(inv.Amount || 0).toLocaleString()}</span>
                                        </div>
                                    </div>
                                    <p className="info-line parent-name-line"><User size={14} /> <span>{inv.ParentName || 'N/A'}</span></p>
                                    <p className="info-line" title={inv.GroupName}><Users size={14} /> <span>{inv.GroupName || 'N/A'}</span></p>
                                    <p className="info-line" title={inv.BranchName}><MapPin size={14} /> <span>{inv.BranchName || 'N/A'}</span></p>
                                    <p className="info-line" title={inv.CourseName}><BookOpen size={14} /> <span>{inv.CourseName || 'N/A'}</span></p>
                                    
                                    <div className="payment-meta-text">
                                        <span className="meta-item"><Wallet size={14} /> Mode of Payment: {inv.ModeOfPayment || 'N/A'}</span>
                                        <span className="meta-item"><Calendar size={14} /> {inv.PaidDate ? new Date(inv.PaidDate).toLocaleDateString() : 'N/A'}</span>
                                    </div>
                                </div>
                            </div>

                            <div className="card-footer">
                                <button
                                    className="view-btn wide"
                                    onClick={() => handleViewReceipt(inv)}
                                    disabled={loadingAction.id === inv.InvoiceId}
                                >
                                    {loadingAction.id === inv.InvoiceId ? (
                                        <div className="btn-spinner"></div>
                                    ) : (
                                        <>
                                            <CheckCircle size={16} />
                                            View Receipt
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* PDF PREVIEW MODAL */}
            {pdfConfig.show && (
                <div className="pdf-modal-overlay">
                    <div className="pdf-modal-container">
                        <div className="pdf-modal-header">
                            <h3>{pdfConfig.title}</h3>
                            <button 
                                className="pdf-close-btn" 
                                onClick={() => {
                                    if (pdfConfig.data) URL.revokeObjectURL(pdfConfig.data);
                                    setPdfConfig({ ...pdfConfig, show: false, data: null });
                                }}
                            >
                                <span>Close</span>
                            </button>
                        </div>
                        <div className="pdf-viewer-wrap">
                            <iframe
                                src={pdfConfig.data}
                                title="Document Preview"
                                frameBorder="0"
                            ></iframe>
                        </div>
                    </div>
                </div>
            )}

            {/* POPUP OVERLAY */}
            {popup.show && (
                <div className="popup-overlay" onClick={() => setPopup({ ...popup, show: false })}>
                    <div className="popup-card" onClick={e => e.stopPropagation()}>
                        <div className={`popup-icon ${popup.type}`}>
                            {popup.type === "success" ? "✓" : "!"}
                        </div>
                        <h2>{popup.title}</h2>
                        <p>{popup.message}</p>
                        <button className="popup-btn" onClick={() => setPopup({ ...popup, show: false })}>
                            Got it
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PaymentHistory;
